import { useEffect, useMemo, type ReactNode } from 'react'
import BottomSheetContent from '../../../components/BottomSheetComponents/BottomSheetContent'
import ImportantNoteIcon from '../components/summary/ImportantNoteIcon'
import { TaxHarvestingBottomSheetName, trackTaxHarvestingBottomSheetLoaded } from '../tracking/taxHarvestingTracking'
import { useQuery } from '@tanstack/react-query'
import { fetchTaxHarvestingSummary } from '../api/taxHarvestingSummary'
import type { TaxHarvestingSummaryResponse } from '../types/summary.types'
import { convertEpochToDate } from '../../../utils/utils'
import { formatFyDisplay } from '../utils/misc'

type Props = {
  onDismiss: () => void
}

type ImportantNoteItem = {
  accessibleLabel: string
  visual: ReactNode
}

export default function ImportantNoteBottomSheet({ onDismiss }: Props) {
  const { data } = useQuery<TaxHarvestingSummaryResponse>({
    queryKey: ['taxHarvestingSummary'],
    queryFn: fetchTaxHarvestingSummary
  })
  const currentFyLabel = useMemo(
    () =>
      formatFyDisplay({
        format: 'short',
        currentFyStart: data?.currentFyStart,
        currentFyEnd: data?.currentFyEnd,
        currentFy: data?.currentFy
      }),
    [data?.currentFyStart, data?.currentFyEnd, data?.currentFy]
  )
  const taxRulesYear = useMemo(
    () => (data?.currentFyEnd ? convertEpochToDate(data.currentFyEnd, '%Y') : ''),
    [data?.currentFyEnd]
  )

  // One accessible name per <li> so TalkBack/VoiceOver do not split on styled spans (Reg_Important_Note_B01)
  const noteItems = useMemo((): ImportantNoteItem[] => {
    const mutualFundsOnlyLabel = currentFyLabel
      ? `This tax summary is calculated for ${currentFyLabel} and only includes your mutual fund investments. Gains or losses from stocks, ETFs etc. are not included.`
      : 'This tax summary only includes your mutual fund investments. Gains or losses from stocks, ETFs etc. are not included.'
    const taxRulesPhrase = taxRulesYear ? `${taxRulesYear} tax rules` : 'tax rules'

    return [
      {
        accessibleLabel:
          'The ₹1.25L LTCG exemption applies across all equity investments (mutual funds, stocks, ETFs).',
        visual: (
          <>
            The ₹1.25L LTCG exemption applies across{' '}
            <span className='text-[#232323]'>all equity investments</span> (mutual funds, stocks, ETFs).
          </>
        )
      },
      {
        accessibleLabel: mutualFundsOnlyLabel,
        visual: currentFyLabel ? (
          <>
            This tax summary is calculated for {currentFyLabel} and{' '}
            <span className='text-[#232323]'>only includes your mutual fund investments</span>. Gains or
            losses from stocks, ETFs etc. are not included.
          </>
        ) : (
          <>
            This tax summary <span className='text-[#232323]'>only includes your mutual fund investments</span>.
            Gains or losses from stocks, ETFs etc. are not included.
          </>
        )
      },
      {
        accessibleLabel:
          'Losses carried forward from previous financial years are not included.',
        visual: (
          <>
            Losses carried forward from previous financial years{' '}
            <span className='text-[#232323]'>are not included</span>.
          </>
        )
      },
      {
        accessibleLabel:
          'Non-equity refers to all fund categories other than equity (debt, hybrid, etc.).',
        visual: 'Non-equity refers to all fund categories other than equity (debt, hybrid, etc.).'
      },
      {
        accessibleLabel: `Calculations are always updated based on your latest fetched portfolio data and ${taxRulesPhrase}. Actual tax may vary depending on NAV at the time of transaction.`,
        visual: (
          <>
            Calculations are always updated based on your latest fetched{' '}
            <span className='text-[#232323]'>portfolio data</span> and{' '}
            <span className='text-[#232323]'>{taxRulesPhrase}</span>. Actual tax may vary depending on NAV
            at the time of transaction.
          </>
        )
      },
      {
        accessibleLabel: 'Calculations account for exit load wherever applicable.',
        visual: (
          <>
            Calculations account for <span className='text-[#232323]'>exit load wherever applicable</span>.
          </>
        )
      },
      {
        accessibleLabel:
          'Since this product covers mutual funds only, please consult your tax advisor for more information.',
        visual: (
          <>
            Since this product covers mutual funds only, please{' '}
            <span className='text-[#232323]'>consult your tax advisor</span> for more information.
          </>
        )
      }
    ]
  }, [currentFyLabel, taxRulesYear])

  useEffect(() => {
    trackTaxHarvestingBottomSheetLoaded(TaxHarvestingBottomSheetName.ImportantNote)
  }, [])

  return (
    <BottomSheetContent
      logoSvg={<ImportantNoteIcon />}
      heading='Important Note'
      bottomSheetBodyClassName='!pl-6'
      onDismiss={onDismiss}
    >
      <div className='text-[14px] font-normal leading-[150%] space-y-4 pb-4 text-[#6A6A6E]'>
        <ol className='list-decimal pl-4 space-y-1 text-[#6A6A6E]'>
          {noteItems.map((note) => (
            <li key={note.accessibleLabel} className='text-[#6A6A6E]'>
              {/*
                Reg_Important_Note_B01: one SR stop per point (full sentence + list position).
                Visible styled spans are hidden from AT; sr-only text is a single node.
              */}
              <span className='sr-only'>{note.accessibleLabel}</span>
              <span aria-hidden='true'>{note.visual}</span>
            </li>
          ))}
        </ol>
      </div>
    </BottomSheetContent>
  )
}
